import React, { useContext } from 'react';
import { Box, Typography, Avatar, keyframes } from "@mui/material";
import dayjs from "dayjs";
import { PAContext } from "../../../Services/PAContext";
import { capitalizeFirstLetter } from "../../Helpers/TextStyling";

// Soft pulse animation for processing state
const pulse = keyframes`
  0% { opacity: 0.7; }
  50% { opacity: 0.4; }
  100% { opacity: 0.7; }
`;

function MessageCard(props) {
    const { dataArray } = useContext(PAContext);
    const { Attendees } = dataArray;
    const { message, align } = props;

    const isMe = align === 'right';
    const isProcessing = message.isProcessing; // The optimistic flag we added

    const sender = Attendees.find((_att) => _att.id === message.attendee_id);
    const nickname = sender?.nickname || "Guest";

    // Use current time if created is 0 (class default) or missing during processing
    const displayTime = (!message.created || message.created === 0)
        ? dayjs()
        : dayjs(message.created);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isMe ? 'flex-end' : 'flex-start',
                mb: 1,
                width: '100%',
                // Apply pulse only if we are waiting for server confirmation
                animation: isProcessing ? `${pulse} 1.5s ease-in-out infinite` : 'none',
                transition: 'all 0.3s ease'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMe ? 'row-reverse' : 'row',
                    alignItems: 'flex-end',
                    maxWidth: '85%',
                    gap: 1
                }}
            >
                {/* Avatar for others */}
                {!isMe && (
                    <Avatar
                        sx={{
                            width: 24,
                            height: 24,
                            fontSize: '9px',
                            fontWeight: 800,
                            bgcolor: '#f1f5f9',
                            color: '#64748b',
                            border: '1px solid #e2e8f0'
                        }}
                    >
                        {nickname.substring(0, 1).toUpperCase()}
                    </Avatar>
                )}

                {/* The Bubble */}
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: isMe ? '16px 16px 2px 16px' : '16px 16px 16px 2px',
                        background: isMe
                            ? (isProcessing ? 'rgba(224, 242, 254, 0.6)' : 'rgba(224, 242, 254, 0.9)')
                            : 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(4px)',
                        color: isProcessing ? '#94a3b8' : '#334155',
                        boxShadow: isProcessing ? 'none' : '0 2px 12px rgba(0,0,0,0.03)',
                        border: isMe ? '1px solid #bae6fd' : '1px solid #f1f5f9',
                        position: 'relative',

                        // Priority styling
                        ...(message.priority && {
                            border: '1px solid #fecaca',
                            background: '#fff1f2',
                            boxShadow: '0 0 10px rgba(251, 113, 133, 0.1)'
                        })
                    }}
                >
                    {/* Nickname for group context */}
                    {!isMe && (
                        <Typography
                            sx={{
                                fontSize: '10px',
                                fontWeight: 800,
                                mb: 0.3,
                                color: '#0ea5e9',
                                letterSpacing: '0.02em'
                            }}
                        >
                            {capitalizeFirstLetter(nickname)}
                        </Typography>
                    )}

                    <Typography
                        sx={{
                            fontSize: '0.875rem',
                            lineHeight: 1.4,
                            fontWeight: 500,
                            color: isProcessing ? '#64748b' : '#475569'
                        }}
                    >
                        {message.message}
                    </Typography>

                    {/* Meta info / Footer of bubble */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        mt: 0.5,
                        opacity: 0.6
                    }}>
                        <Typography sx={{ fontSize: '9px', fontWeight: 700 }}>
                            {isProcessing ? "Sending..." : displayTime.format('h:mm A')}
                        </Typography>

                        {message.priority && !isProcessing && (
                            <i className="icon-warning" style={{ fontSize: '10px', marginLeft: '4px', color: '#f43f5e' }} />
                        )}

                        {/* Tiny checkmark if sent successfully */}
                        {isMe && !isProcessing && (
                            <i className="icon-check" style={{ fontSize: '10px', marginLeft: '4px', color: '#22c55e' }} />
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default MessageCard;